import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  BookOpen,
  Briefcase,
  Building2,
  GraduationCap,
  Megaphone,
  PackageCheck,
  ShieldCheck,
  Smartphone,
  Trophy,
  UserRound,
  Users,
  Wallet,
} from 'lucide-react'

import { PageIntro } from '@/components/page-intro'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/cn'

interface Langkah {
  judul: string
  isi: string
}

interface Bagian {
  id: string
  judul: string
  ringkas: string
  icon: React.ReactNode
  langkah: Langkah[]
  catatan?: string
}

/**
 * Isi panduan ditulis sebagai data, bukan sebagai markup.
 *
 * Daftar isi, penanda bagian yang sedang dibaca, dan penomoran langkah
 * seluruhnya diturunkan dari susunan ini — sehingga menambah satu bagian
 * cukup dikerjakan di satu tempat, dan ketiganya tidak mungkin berbeda.
 */
const bagian: Bagian[] = [
  {
    id: 'mulai',
    judul: 'Mulai di sini',
    ringkas: 'Membuat akun dan memilih peran yang sesuai dengan yang ingin Anda kerjakan.',
    icon: <UserRound className="size-4" />,
    langkah: [
      {
        judul: 'Buat akun',
        isi: 'Buka Daftar, isi nama, email, dan kata sandi minimal 8 karakter. Akun langsung aktif; tidak ada masa tunggu.',
      },
      {
        judul: 'Verifikasi email',
        isi: 'Verifikasi dibutuhkan sebelum transaksi pertama Anda. Tautannya dikirim ke email yang Anda daftarkan.',
      },
      {
        judul: 'Pilih peran',
        isi: 'Peserta untuk belajar, Tenaga Ahli untuk menjual jasa dan mengajar, Klien untuk memesan pekerjaan, Perekrut untuk memasang lowongan. Satu akun boleh memegang beberapa peran sekaligus.',
      },
      {
        judul: 'Lengkapi profil',
        isi: 'Foto profil, keahlian, dan portofolio. Ini yang dilihat calon klien sebelum memutuskan menghubungi Anda.',
      },
    ],
  },
  {
    id: 'belajar',
    judul: 'Belajar di kelas',
    ringkas: 'Menemukan kelas, membelinya, dan mengikutinya sampai selesai.',
    icon: <GraduationCap className="size-4" />,
    langkah: [
      {
        judul: 'Telusuri katalog',
        isi: 'Buka Kelas, saring menurut kata kunci atau kategori. Setiap kelas menampilkan kurikulum lengkapnya sebelum Anda membayar, dan satu materi dapat dibuka sebagai pratinjau gratis.',
      },
      {
        judul: 'Beli kelas',
        isi: 'Tekan Beli kelas ini, lalu bayar lewat halaman Midtrans — QRIS, virtual account, e-wallet, atau kartu. Kelas terbuka otomatis begitu pembayaran terkonfirmasi.',
      },
      {
        judul: 'Ikuti materinya',
        isi: 'Buka Kelas saya, pilih kelasnya, lalu tekan satu materi untuk membacanya. Tandai selesai setiap kali Anda tuntas; kemajuan Anda tersimpan sendiri.',
      },
      {
        judul: 'Ikut sesi langsung',
        isi: 'Kelas berformat langsung punya jadwal sesi beserta tautan Google Meet atau Zoom. Tautannya muncul lima belas menit sebelum sesi dimulai, dan hanya bagi peserta yang terdaftar.',
      },
      {
        judul: 'Tulis ulasan',
        isi: 'Setelah mengikuti kelas, Anda dapat memberi nilai dan ulasan. Ulasan hanya dapat ditulis peserta yang benar-benar terdaftar.',
      },
    ],
    catatan:
      'Akses kelas berlaku seumur waktu selama kelasnya masih tayang. Materi berjadwal terbuka mengikuti tanggal yang ditetapkan pengajar.',
  },
  {
    id: 'jasa',
    judul: 'Menjual paket jasa',
    ringkas: 'Memasang etalase jasa dengan harga dan lama pengerjaan yang tertulis sejak awal.',
    icon: <PackageCheck className="size-4" />,
    langkah: [
      {
        judul: 'Buat etalase',
        isi: 'Buka Jasa saya, tekan Buat etalase, isi judul, kategori, dan penjelasan pekerjaannya.',
      },
      {
        judul: 'Susun paket',
        isi: 'Sediakan sampai tiga tingkat: Basic, Standard, dan Premium. Setiap tingkat punya harganya sendiri, lama pengerjaan, jumlah revisi, dan daftar yang termasuk di dalamnya.',
      },
      {
        judul: 'Tayangkan',
        isi: 'Tekan Terbitkan setelah semua kolom wajib terisi. Halaman kesiapan menunjukkan apa saja yang masih kurang.',
      },
      {
        judul: 'Kerjakan pesanan',
        isi: 'Pesanan yang masuk muncul di Pesanan jasa. Dana pembeli ditahan escrow sampai Anda menyerahkan hasilnya dan pembeli menerimanya.',
      },
    ],
  },
  {
    id: 'proyek',
    judul: 'Papan proyek dan escrow',
    ringkas: 'Memasang kebutuhan, menerima proposal, dan membayar bertahap lewat escrow.',
    icon: <Briefcase className="size-4" />,
    langkah: [
      {
        judul: 'Pasang proyek',
        isi: 'Sebagai Klien, buka Proyek saya lalu Posting proyek. Sebutkan lingkup pekerjaan, anggaran, dan tenggatnya.',
      },
      {
        judul: 'Terima proposal',
        isi: 'Tenaga ahli mengirim proposal beserta rencana pengerjaan dan nilai penawarannya. Bandingkan, lalu terima satu.',
      },
      {
        judul: 'Isi escrow',
        isi: 'Kontrak terbit setelah proposal diterima. Anda menyetor dana per tahap ke escrow — dana itu belum menjadi milik siapa pun sampai tahapnya disetujui.',
      },
      {
        judul: 'Setujui tahap',
        isi: 'Tenaga ahli menyerahkan hasil tiap tahap. Anda menyetujuinya, meminta revisi, atau membuka sengketa. Dana baru berpindah ketika Anda menyetujui.',
      },
      {
        judul: 'Bila terjadi sengketa',
        isi: 'Sengketa ditangani mediator. Kedua pihak mengirim bukti, lalu mediator memutuskan pembagian dananya.',
      },
    ],
    catatan:
      'Escrow ada untuk melindungi keduanya: klien tidak membayar pekerjaan yang belum ada, dan tenaga ahli tidak mengerjakan sesuatu yang dananya belum disiapkan.',
  },
  {
    id: 'sayembara',
    judul: 'Sayembara',
    ringkas: 'Mengumpulkan banyak karya sekaligus, lalu memilih satu pemenang.',
    icon: <Trophy className="size-4" />,
    langkah: [
      {
        judul: 'Buat sayembara',
        isi: 'Sebutkan yang Anda cari, hadiahnya, dan tanggal penutupannya. Hadiah disetor di muka supaya peserta tahu ia sungguhan.',
      },
      {
        judul: 'Kirim karya',
        isi: 'Sebagai tenaga ahli, buka sayembara yang terbuka lalu kirim karya Anda sebelum penutupan.',
      },
      {
        judul: 'Pilih pemenang',
        isi: 'Setelah tertutup, penyelenggara menyaring dan memilih pemenangnya. Hadiah langsung masuk ke dompet pemenang.',
      },
    ],
  },
  {
    id: 'lowongan',
    judul: 'Lowongan dan perusahaan',
    ringkas: 'Memasang lowongan atas nama perusahaan, dan melamar sebagai pencari kerja.',
    icon: <Building2 className="size-4" />,
    langkah: [
      {
        judul: 'Buat profil perusahaan',
        isi: 'Buka Perusahaan & lowongan lalu buat profilnya. Unggah logo lewat ikon kamera pada gambarnya — logo membuat lowongan Anda jauh lebih dipercaya.',
      },
      {
        judul: 'Pasang lowongan',
        isi: 'Sebutkan posisi, jenis kerja, lokasi, dan rentang gaji. Draf dapat disimpan dulu sebelum ditayangkan.',
      },
      {
        judul: 'Kelola pelamar',
        isi: 'Pelamar masuk ke papan tahapan: dilamar, disaring, wawancara, ditawari, diterima. Geser kartunya sesuai kemajuan.',
      },
      {
        judul: 'Melamar',
        isi: 'Sebagai pencari kerja, buka Lowongan, simpan yang menarik, dan kirim lamaran. Pantau kemajuannya di Lamaran saya.',
      },
    ],
  },
  {
    id: 'portofolio',
    judul: 'Portofolio dan direktori talenta',
    ringkas: 'Menunjukkan karya Anda supaya calon klien menemukan dan mempercayainya.',
    icon: <BookOpen className="size-4" />,
    langkah: [
      {
        judul: 'Tambah karya',
        isi: 'Buka Profil, gulir ke Portofolio, tekan Tambah. Isi judul, penjelasan singkat, dan tautan karyanya bila ada.',
      },
      {
        judul: 'Unggah foto',
        isi: 'Tekan ikon kamera pada karya untuk mengunggah fotonya — JPG, PNG, atau WebP, maksimal 3 MB. Karya berfoto jauh lebih sering dibuka daripada yang hanya bertautan.',
      },
      {
        judul: 'Tampil sebagai carousel',
        isi: 'Karya Anda tampil sebagai deretan yang digulir menyamping di halaman profil publik. Pengunjung dapat mengetuknya untuk melihat versi besarnya.',
      },
      {
        judul: 'Muncul di direktori',
        isi: 'Profil dengan keahlian dan tarif yang terisi muncul di Direktori talenta, tempat klien mencari orang untuk pekerjaannya.',
      },
    ],
    catatan: 'Portofolio dibatasi 24 karya. Pilih yang terbaik — daftar yang terlalu panjang justru jarang dibaca sampai habis.',
  },
  {
    id: 'promosi',
    judul: 'Promosi berbayar',
    ringkas: 'Memasang karya atau penawaran Anda di headline beranda.',
    icon: <Megaphone className="size-4" />,
    langkah: [
      {
        judul: 'Pilih lama tayang',
        isi: 'Buka Promosi saya, tekan Pasang promosi. Tersedia paket 7, 14, dan 30 hari — harganya tertera sebelum Anda membayar.',
      },
      {
        judul: 'Susun tayangannya',
        isi: 'Judul, keterangan singkat, gambar, teks tombol, dan tautan tujuannya. Judul yang menyebutkan apa yang Anda tawarkan jauh lebih sering diklik daripada yang hanya menyebut nama usaha.',
      },
      {
        judul: 'Bayar',
        isi: 'Anda dibawa ke halaman Midtrans. Promosi mulai tayang begitu pembayaran terkonfirmasi.',
      },
      {
        judul: 'Pantau hasilnya',
        isi: 'Halaman Promosi saya menampilkan jumlah tayang, jumlah klik, dan rasionya per promosi.',
      },
    ],
    catatan:
      'Masa tayang dihitung sejak pembayaran terkonfirmasi, bukan sejak Anda memesannya — jadi menunda membayar tidak memotong jatah hari Anda. Promosi yang isinya menyesatkan dapat dihentikan tim kami.',
  },
  {
    id: 'dompet',
    judul: 'Dompet dan pencairan',
    ringkas: 'Ke mana dana masuk, kapan dapat dicairkan, dan bagaimana menariknya.',
    icon: <Wallet className="size-4" />,
    langkah: [
      {
        judul: 'Baca saldo',
        isi: 'Saldo tersedia dapat dicairkan. Tertahan escrow adalah dana proyek yang belum disetujui klien. Dalam masa tahan adalah penjualan kelas yang belum melewati masa pengembalian.',
      },
      {
        judul: 'Daftarkan rekening',
        isi: 'Tambahkan rekening bank atas nama Anda sendiri. Rekening atas nama orang lain akan ditolak saat pemeriksaan.',
      },
      {
        judul: 'Ajukan pencairan',
        isi: 'Ajukan dari saldo tersedia. Tim keuangan memeriksanya sebelum dana dikirim, jadi ada jeda satu sampai dua hari kerja.',
      },
    ],
  },
  {
    id: 'pembayaran',
    judul: 'Pembayaran',
    ringkas: 'Cara membayar dan tempat memeriksa riwayatnya.',
    icon: <ShieldCheck className="size-4" />,
    langkah: [
      {
        judul: 'Satu halaman pembayaran',
        isi: 'Semua pembayaran melewati Midtrans. Anda tidak memilih metode di sini — pilihannya ada di halaman Midtrans: QRIS, virtual account semua bank besar, e-wallet, dan kartu.',
      },
      {
        judul: 'Riwayat',
        isi: 'Buka Pesanan untuk melihat seluruh pembayaran beserta cara, waktu, dan nomor rujukannya.',
      },
      {
        judul: 'Bila pembayaran tertahan',
        isi: 'Status diperbarui otomatis begitu Midtrans mengabarkan hasilnya. Bila sudah membayar tetapi status belum berubah setelah lima belas menit, hubungi kami dengan nomor pesanannya.',
      },
    ],
    catatan: 'skava.id tidak pernah menyimpan nomor kartu Anda. Seluruh data kartu ditangani Midtrans.',
  },
  {
    id: 'seluler',
    judul: 'Aplikasi Android',
    ringkas: 'Versi ponsel untuk belajar dan memantau pekerjaan dari mana saja.',
    icon: <Smartphone className="size-4" />,
    langkah: [
      {
        judul: 'Pasang',
        isi: 'Unduh APK-nya, buka berkasnya di ponsel, lalu izinkan pemasangan dari sumber tidak dikenal ketika diminta.',
      },
      {
        judul: 'Jelajahi tanpa akun',
        isi: 'Katalog kelas, paket jasa, papan proyek, sayembara, lowongan, dan direktori talenta dapat dibuka sebelum Anda masuk.',
      },
      {
        judul: 'Belajar di ponsel',
        isi: 'Materi kelas dapat dibaca lengkap, termasuk blok kode. Tandai selesai untuk melanjutkan ke materi berikutnya.',
      },
      {
        judul: 'Atur tampilan',
        isi: 'Profil, bagian Tampilan: terang, gelap, atau mengikuti peranti. Bawaannya terang, sama dengan situs ini.',
      },
    ],
  },
  {
    id: 'admin',
    judul: 'Untuk pengelola',
    ringkas: 'Menu yang hanya terlihat bagi pemegang peran internal.',
    icon: <Users className="size-4" />,
    langkah: [
      {
        judul: 'Pengguna',
        isi: 'Tambah akun, atur peran, setel ulang kata sandi, nonaktifkan, dan hapus. Kata sandi yang dibuatkan sistem hanya ditampilkan sekali.',
      },
      {
        judul: 'Slideshow & iklan',
        isi: 'Atur slide beranda dan banner iklan beserta jadwal tayang. Hitungan tayang dan klik tercatat per tayangan.',
      },
      {
        judul: 'Antrean pencairan',
        isi: 'Peran Finance memeriksa dan menyetujui pencairan sebelum dana dikirim.',
      },
      {
        judul: 'Mediasi sengketa',
        isi: 'Peran Mediator membaca bukti kedua pihak lalu memutuskan pembagian dana escrow.',
      },
    ],
  },
  {
    id: 'keamanan',
    judul: 'Menjaga akun',
    ringkas: 'Hal-hal kecil yang menentukan akun Anda aman atau tidak.',
    icon: <Megaphone className="size-4" />,
    langkah: [
      {
        judul: 'Kata sandi',
        isi: 'Pakai kata sandi yang tidak Anda pakai di tempat lain. Ganti lewat Profil, Ganti kata sandi.',
      },
      {
        judul: 'Lupa kata sandi',
        isi: 'Gunakan tautan Lupa kata sandi di halaman masuk. Tautan pemulihan berlaku satu jam dan hanya sekali pakai.',
      },
      {
        judul: 'Perangkat yang masuk',
        isi: 'Profil menampilkan perangkat yang sedang masuk. Cabut yang tidak Anda kenali; mengganti kata sandi mencabut semuanya sekaligus.',
      },
      {
        judul: 'Waspada penipuan',
        isi: 'Kami tidak pernah meminta kata sandi lewat WhatsApp maupun email. Pembayaran hanya melalui halaman Midtrans di dalam skava.id — tidak pernah transfer langsung ke rekening pribadi.',
      },
    ],
  },
]

export function PanduanPage() {
  const [aktif, setAktif] = useState(bagian[0]!.id)

  // Bagian yang sedang dibaca ditandai pada daftar isi. Ambang atas layar
  // dipakai, bukan tengahnya, supaya penandanya berpindah tepat ketika judul
  // bagian berikutnya sampai di puncak.
  useEffect(() => {
    const pengamat = new IntersectionObserver(
      (entries) => {
        const terlihat = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
        if (terlihat) setAktif(terlihat.target.id)
      },
      { rootMargin: '-96px 0px -70% 0px' },
    )

    for (const b of bagian) {
      const el = document.getElementById(b.id)
      if (el) pengamat.observe(el)
    }
    return () => pengamat.disconnect()
  }, [])

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 lg:py-12">
      <PageIntro
        accent="kelas"
        eyebrow="Panduan"
        icon={<BookOpen className="size-3.5" />}
        title="Panduan lengkap skava.id"
        description="Seluruh fitur beserta cara memakainya, disusun mengikuti urutan yang biasanya Anda kerjakan."
      />

      <div className="mt-10 grid gap-10 lg:grid-cols-[16rem_1fr] [&>*]:min-w-0">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <p className="mono-label mb-3 text-[11px] text-subtle">Daftar isi</p>
          <nav className="thin-scroll -mx-2 flex gap-1 overflow-x-auto px-2 pb-2 lg:flex-col lg:overflow-visible lg:pb-0">
            {bagian.map((b) => (
              <a
                key={b.id}
                href={`#${b.id}`}
                aria-current={aktif === b.id ? 'true' : undefined}
                className={cn(
                  'flex shrink-0 items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-semibold transition lg:shrink',
                  aktif === b.id
                    ? 'bg-brand-soft text-brand-ink'
                    : 'text-muted hover:bg-sunken hover:text-ink',
                )}
              >
                {b.icon}
                <span className="whitespace-nowrap lg:whitespace-normal">{b.judul}</span>
              </a>
            ))}
          </nav>
        </aside>

        <div className="space-y-12">
          {bagian.map((b) => (
            <section key={b.id} id={b.id} className="scroll-mt-24">
              <h2 className="text-2xl font-extrabold tracking-tight text-ink">{b.judul}</h2>
              <p className="mt-1.5 text-muted">{b.ringkas}</p>

              <ol className="mt-6 space-y-5">
                {b.langkah.map((l, index) => (
                  <li key={l.judul} className="flex gap-4">
                    <span className="mono-label mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-sunken text-[11px] text-brand tabular-nums">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div className="min-w-0">
                      <p className="font-bold text-ink">{l.judul}</p>
                      <p className="mt-0.5 leading-relaxed text-muted">{l.isi}</p>
                    </div>
                  </li>
                ))}
              </ol>

              {b.catatan ? (
                <p className="mt-6 rounded-xl border-l-4 border-brand bg-sunken px-4 py-3 text-sm text-muted">
                  {b.catatan}
                </p>
              ) : null}
            </section>
          ))}

          <section className="card-hard hard-dark bg-arang-900 p-6 sm:p-8">
            <p className="mono-label text-[13px] text-white/60">Masih ada yang belum jelas</p>
            <h2 className="display mt-3 text-2xl text-white sm:text-3xl">Tanya langsung saja.</h2>
            <p className="mt-2 max-w-lg text-white/70">
              Kirim pesan ke WhatsApp 082243408054 atau surel masmikelku@gmail.com. Sebutkan halaman yang
              sedang Anda buka supaya jawabannya cepat.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://wa.me/6282243408054" target="_blank" rel="noopener noreferrer">
                <Button variant="inverted">Hubungi lewat WhatsApp</Button>
              </a>
              <Link to="/daftar">
                <Button variant="outline" className="border-white/40 text-white hover:bg-white hover:text-arang-900">
                  Buat akun
                </Button>
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
